import { SearchParameters } from 'data/conversational-search/types';
import {
  ProvinceAndStateNameMappings,
  countryCodeFromProvinceOrState,
  ProvinceOrStateCode,
  provinceOrStateCodeFromString,
} from 'utils/province_or_state';
import { fetchSearchPredictionsV1 } from 'data/search/predictions/fetcher';
import { AutocompletePredictionPredictionCategory } from 'utils/google-maps/types';
import { CountryCode, CountryCodeList } from 'types/countries';

/**
 * Free-text country mentions used to derive a country anchor when the query
 * doesn't reference a specific province/state.
 */
const COUNTRY_NAME_PATTERNS: ReadonlyArray<{ pattern: RegExp; country: CountryCode }> = [
  { pattern: /\b(canada|canadian)\b/i, country: CountryCodeList.CANADA },
  { pattern: /\b(usa|u\.s\.a\.?|u\.s\.|united\s+states|america|american)\b/i, country: CountryCodeList.UNITED_STATES },
];

interface LocationSignal {
  countryCode: CountryCode;
  /** undefined when only a bare country word ("Canada", "USA") was found. */
  provinceCode?: ProvinceOrStateCode;
}

/**
 * Detect an explicit country anchor in the query. We only call the geocoding
 * API when one of these signals is present — without an anchor the API call
 * is essentially guessing, so we'd rather fall through to the AI.
 *
 * Precedence: "City, ST" pattern > full province/state name > country word.
 */
function detectLocationSignal(query: string): LocationSignal | null {
  const commaCodeMatch = query.match(/,\s*([A-Za-z]{2})\b/);
  if (commaCodeMatch) {
    const provinceCode = provinceOrStateCodeFromString(commaCodeMatch[1]);
    if (provinceCode) {
      return {
        provinceCode,
        countryCode: countryCodeFromProvinceOrState(provinceCode),
      };
    }
  }

  for (const [name, code] of Object.entries(ProvinceAndStateNameMappings)) {
    const pattern = new RegExp(`\\b${name.replace(/\s+/g, '\\s+')}\\b`, 'i');
    if (pattern.test(query)) {
      return {
        provinceCode: code,
        countryCode: countryCodeFromProvinceOrState(code),
      };
    }
  }

  for (const { pattern, country } of COUNTRY_NAME_PATTERNS) {
    if (pattern.test(query)) {
      return { countryCode: country };
    }
  }

  return null;
}

/**
 * Strip filter-related tokens (prices, sqft, property types, intents, fillers,
 * etc.) so the leftover string is the location-shaped portion of the query
 * suitable to send to the Go-Search predictions API.
 *
 * Keep this strip list in sync with the keywords matched by the other
 * extractors — anything not stripped here gets sent as API noise.
 */
function stripNonLocationTokens(query: string): string {
  let s = query;
  s = s.replace(/\$\s*\d+(?:[.,]\d+)?\s*[km]?\b/gi, ' ');
  s = s.replace(/\b\d+(?:[.,]\d+)?\s*[km]\b/gi, ' ');
  s = s.replace(/\b\d+\s*\+?\s*(?:bedrooms?|beds?|br|bathrooms?|baths?|ba|parking\s+spaces?|parking\s+spots?|car\s+garages?)\b/gi, ' ');
  s = s.replace(/\b\d+(?:\s*(?:to|-)\s*\d+)?\s*(?:sq\s*ft|sqft|square\s+(?:feet|foot)|sf)\b/gi, ' ');
  s = s.replace(/\b(?:homes?|houses?|detached|single[\s-]family|bungalows?|condos?|apartments?|condominiums?|townhouses?|townhomes?|row\s+houses?|farms?|farmhouses?|acreages?|land|lots?|vacant\s+land|commercial|office|retail|industrial|real\s+estate|propert(?:y|ies)|listings?|places?)\b/gi, ' ');
  s = s.replace(/\b(?:garage|pool|swimming\s+pool|fireplaces?|waterfront|lakefront|oceanfront|beachfront|open\s+house)\b/gi, ' ');
  s = s.replace(/\b(?:under|over|below|above|less\s+than|more\s+than|between|budget|max(?:imum)?|min(?:imum)?|up\s+to|at\s+least|from|exactly)\b/gi, ' ');
  s = s.replace(/\b(?:for\s+rent|rentals?|to\s+lease|to\s+rent|for\s+sale|to\s+buy|to\s+purchase|recently\s+sold|sold|available)\b/gi, ' ');
  // Strip short prepositions that double as US/CA state codes only when they
  // aren't sitting in the ", ST" state-code position — otherwise we'd erase
  // the disambiguator we just used to derive the country.
  s = s.replace(/(?<!,\s*)\b(?:in|on|or|me)\b/gi, ' ');
  s = s.replace(/\b(?:at|near|around|with|the|a|an|of|by|for|please|show|find|i|want|need|looking|some|any|and|to)\b/gi, ' ');
  s = s.replace(/[!?;]+/g, ' ');
  s = s.replace(/\s+/g, ' ').trim();
  s = s.replace(/^,+|,+$/g, '').trim();
  return s;
}

/**
 * True when the candidate string is exactly the inferred province/state's
 * full name. In that case the API can't add any information beyond what we
 * already know from the regex, so we skip the network call.
 */
function isJustProvinceName(candidate: string, expected: ProvinceOrStateCode): boolean {
  const normalized = candidate.toLowerCase().trim();
  for (const [name, code] of Object.entries(ProvinceAndStateNameMappings)) {
    if (code === expected && name.toLowerCase() === normalized) return true;
  }
  return false;
}

/**
 * Parse a Go-Search prediction's description ("Toronto, ON" or
 * "Yorkville, Toronto, ON") into structured location params.
 *
 * Returns null when the description doesn't end in a recognizable
 * province/state code — predictions outside our tenant geography are
 * unusable for the filter object.
 */
function parsePredictionDescription(
  description: string,
  label: AutocompletePredictionPredictionCategory | string | null
): Pick<SearchParameters, 'city' | 'neighbourhood' | 'province' | 'country'> | null {
  const parts = description.split(',').map(p => p.trim()).filter(Boolean);
  if (parts.length === 0) return null;

  const lastPart = parts[parts.length - 1];
  const provinceCode = provinceOrStateCodeFromString(lastPart);
  if (!provinceCode) return null;

  const base: Pick<SearchParameters, 'province' | 'country'> = {
    province: provinceCode.toUpperCase(),
    country: countryCodeFromProvinceOrState(provinceCode),
  };

  if (parts.length === 1 || label === AutocompletePredictionPredictionCategory.PROVINCE) {
    return base;
  }

  if (label === AutocompletePredictionPredictionCategory.NEIGHBORHOOD && parts.length >= 3) {
    return { ...base, neighbourhood: parts[0], city: parts[1] };
  }

  return { ...base, city: parts[0] };
}

/**
 * Resolves the location portion of a query using the Go-Search predictions
 * API. The API is consulted only when the query has an explicit country
 * anchor (province/state name, "City, ST" pattern, or country word) — bare
 * city names without context fall through to null so the AI can handle them
 * with conversation history.
 *
 * Falls back to the regex-inferred province/country when the API call fails
 * or returns no usable prediction.
 */
async function extractLocation(
  query: string
): Promise<Pick<SearchParameters, 'city' | 'neighbourhood' | 'province' | 'country'> | null> {
  const signal = detectLocationSignal(query);
  if (!signal) return null;

  const buildFallback = (): Pick<SearchParameters, 'province' | 'country'> => {
    const fallback: Pick<SearchParameters, 'province' | 'country'> = { country: signal.countryCode };
    if (signal.provinceCode) fallback.province = signal.provinceCode.toUpperCase();
    return fallback;
  };

  const candidate = stripNonLocationTokens(query);

  // No specific candidate to resolve — emit whatever the regex anchor gave us.
  // Also short-circuits country-only signals (e.g. "homes in Canada") where
  // the API has no useful city-level data to add.
  if (!candidate || !signal.provinceCode || isJustProvinceName(candidate, signal.provinceCode)) {
    return buildFallback();
  }

  try {
    const response = await fetchSearchPredictionsV1({
      query: candidate,
      country: signal.countryCode,
    });

    const locationPrediction = response?.data?.find(p => p.attributes.group === 'locations');
    if (locationPrediction) {
      const parsed = parsePredictionDescription(
        locationPrediction.attributes.description,
        locationPrediction.attributes.label
      );
      if (parsed) return parsed;
    }
  } catch (error) {
    console.error('extractLocation: predictions API failed', error);
  }

  return buildFallback();
}

/**
 * Extract every property type mentioned in the query. A single query can
 * legitimately mention several ("houses and condos", "townhouses, condos,
 * or apartments"), so we collect all matches rather than picking the first.
 *
 * When any type is matched we return a COMPLETE homeType object with every
 * unmentioned type set to `false`. This is required because the default
 * filter state has every property type set to `true` (i.e. "show all"); a
 * partial result like `{ house: true }` would be additively merged into the
 * all-true baseline and silently no-op. Emitting explicit `false` values
 * forces the merge to narrow the filter to just the mentioned types.
 *
 * The trade-off: a deterministic match always REPLACES homeType. Nuanced
 * "also include townhouses" intent stays with the AI path, where the parser
 * doesn't fire because the language is conversational.
 *
 * Returns null when the query doesn't mention any property type — we don't
 * emit a homeType field unless the user specifically asked for one.
 */
function extractHomeType(query: string): SearchParameters['homeType'] | null {
  // "open house" is a feature flag, not a property type — strip it so the
  // house pattern below doesn't get a false hit.
  let lower = query.toLowerCase().replace(/\bopen\s+house\b/g, ' ');
  const matched: NonNullable<SearchParameters['homeType']> = {};

  // Townhouse and farmhouse synonyms ("row houses", "farmhouses") contain
  // the literal "houses". Check and strip them first so they don't also
  // trigger the generic house pattern.
  if (/\b(?:townhouses?|townhomes?|row\s+houses?)\b/.test(lower)) {
    matched.townhouse = true;
    lower = lower.replace(/\b(?:townhouses?|townhomes?|row\s+houses?)\b/g, ' ');
  }
  if (/\b(?:farms?|farmhouses?|acreages?)\b/.test(lower)) {
    matched.farm = true;
    lower = lower.replace(/\b(?:farms?|farmhouses?|acreages?)\b/g, ' ');
  }
  if (/\b(?:houses?|detached|single\s+family|single-family|bungalows?)\b/.test(lower)) {
    matched.house = true;
  }
  if (/\b(?:condos?|apartments?|condominiums?)\b/.test(lower)) {
    matched.condo = true;
  }
  if (/\b(?:vacant\s+land|land|lots?)\b/.test(lower)) {
    matched.land = true;
  }
  if (/\b(?:commercial|office|retail|industrial)\b/.test(lower)) {
    matched.commercial = true;
  }

  if (Object.keys(matched).length === 0) return null;

  return {
    house: false,
    condo: false,
    townhouse: false,
    farm: false,
    land: false,
    commercial: false,
    ...matched,
  };
}

/**
 * English number words we resolve to digits before extraction. Capped at five
 * because higher counts are essentially never spelled out in real-estate
 * search queries (people write "10+ bedrooms", not "ten bedrooms"), and the
 * smaller surface keeps false positives near zero.
 */
const NUMBER_WORDS: Record<string, number> = {
  one: 1, two: 2, three: 3, four: 4, five: 5,
};
const NUMBER_WORD_PATTERN = new RegExp(`\\b(${Object.keys(NUMBER_WORDS).join('|')})\\b`, 'gi');

/**
 * Convert English number words ("three") to digits ("3") so the downstream
 * regex extractors can match them uniformly. Also normalizes a hyphen between
 * a digit and a letter ("3-bedroom") to a space, so "three-bedroom" works
 * after the word→digit pass without needing to widen every extractor pattern.
 */
function normalizeNumberWords(query: string): string {
  let result = query.replace(NUMBER_WORD_PATTERN, match => String(NUMBER_WORDS[match.toLowerCase()]));
  result = result.replace(/(\d)-(?=[a-z])/gi, '$1 ');
  return result;
}

interface PriceRange {
  priceMin?: number;
  priceMax?: number;
}

/**
 * Strict price extractor: requires either a `$` symbol, a `k`/`m` suffix, or
 * an explicit money keyword (`under`, `over`, `between`, `budget`, `up to`,
 * `at least`). Bare numbers without these signals are ignored to avoid
 * misclassifying square footage or year values as prices.
 */
function extractPriceRange(query: string): PriceRange | null {
  const original = query.toLowerCase();
  // Mask out sqft phrases so qualifiers like "over 2000 sqft" don't get
  // misread as prices by the over/under extractors below.
  const lower = original.replace(/\d+\s*(?:to\s+\d+\s*)?(?:sq\s*ft|sqft|square\s+(?:feet|foot)|sf)\b/g, '');
  const hasMoneySignal = /[$]|\d\s*[km]\b|\b(under|over|between|budget|up\s+to|at\s+least|maximum|minimum|max|min|from)\b/.test(lower);
  if (!hasMoneySignal) {
    return null;
  }

  const parseAmount = (raw: string, unitHint?: string): number => {
    const value = parseFloat(raw);
    const unit = (unitHint || '').toLowerCase();
    if (unit === 'm') return value * 1_000_000;
    if (unit === 'k') return value * 1_000;
    if (value < 1_000) return value * 1_000; // bare "$800" in chat almost always means $800k
    return value;
  };

  const numberToken = '\\$?\\s*(\\d+(?:[.,]\\d+)?)\\s*([km])?';

  const rangePatterns = [
    new RegExp(`between\\s+${numberToken}\\s+(?:and|to|-)\\s+${numberToken}`, 'i'),
    new RegExp(`${numberToken}\\s+to\\s+${numberToken}`, 'i'),
    // Hyphen separator allows zero spaces ("500k-800k") — "to" still requires
    // whitespace because we don't want to glue arbitrary tokens together.
    new RegExp(`${numberToken}\\s*-\\s*${numberToken}`, 'i'),
  ];
  for (const pattern of rangePatterns) {
    const match = lower.match(pattern);
    if (match) {
      const priceMin = parseAmount(match[1].replace(/,/g, ''), match[2]);
      const priceMax = parseAmount(match[3].replace(/,/g, ''), match[4]);
      if (priceMin < priceMax) {
        return { priceMin, priceMax };
      }
    }
  }

  const maxPattern = new RegExp(`(?:under|below|less\\s+than|max(?:imum)?|up\\s+to|<=?)\\s*${numberToken}`, 'i');
  const maxMatch = lower.match(maxPattern);
  if (maxMatch) {
    return { priceMax: parseAmount(maxMatch[1].replace(/,/g, ''), maxMatch[2]) };
  }

  const minPattern = new RegExp(`(?:over|above|more\\s+than|min(?:imum)?|at\\s+least|from|>=?)\\s*${numberToken}`, 'i');
  const minMatch = lower.match(minPattern);
  if (minMatch) {
    return { priceMin: parseAmount(minMatch[1].replace(/,/g, ''), minMatch[2]) };
  }

  // Bare "$800k" / "1.5M" with no qualifier — interpret as a max in chat context.
  const bareWithUnit = lower.match(/\$\s*(\d+(?:[.,]\d+)?)\s*([km])?\b|\b(\d+(?:[.,]\d+)?)\s*([km])\b/i);
  if (bareWithUnit) {
    const numStr = bareWithUnit[1] ?? bareWithUnit[3];
    const unit = bareWithUnit[2] ?? bareWithUnit[4];
    if (numStr) {
      return { priceMax: parseAmount(numStr.replace(/,/g, ''), unit) };
    }
  }

  return null;
}

interface SqftRange {
  sqftMin?: number;
  sqftMax?: number;
}

function extractSqftRange(query: string): SqftRange | null {
  const lower = query.toLowerCase();
  const sqftToken = '\\b(?:sq\\s*ft|sqft|square\\s+(?:feet|foot)|sf)\\b';

  const rangeMatch = lower.match(new RegExp(`(\\d{3,5})\\s*(?:to|-)\\s*(\\d{3,5})\\s*${sqftToken}`));
  if (rangeMatch) {
    return { sqftMin: parseInt(rangeMatch[1], 10), sqftMax: parseInt(rangeMatch[2], 10) };
  }

  const overMatch = lower.match(new RegExp(`(?:over|above|at\\s+least|more\\s+than|min(?:imum)?)\\s+(\\d{3,5})\\s*${sqftToken}`));
  if (overMatch) {
    return { sqftMin: parseInt(overMatch[1], 10) };
  }

  const underMatch = lower.match(new RegExp(`(?:under|below|less\\s+than|max(?:imum)?|up\\s+to)\\s+(\\d{3,5})\\s*${sqftToken}`));
  if (underMatch) {
    return { sqftMax: parseInt(underMatch[1], 10) };
  }

  const plainMatch = lower.match(new RegExp(`(\\d{3,5})\\+?\\s*${sqftToken}`));
  if (plainMatch) {
    return { sqftMin: parseInt(plainMatch[1], 10) };
  }

  return null;
}

/**
 * Extract bedroom count. Defaults to a minimum (`+`) unless the user said
 * "exactly N". Returns null when no bedroom phrase is found.
 */
function extractBedrooms(query: string): string | null {
  const match = query.toLowerCase().match(/(\d+)\s*\+?\s*(?:bedrooms?|beds?|br)\b/);
  if (!match) return null;
  const isExact = /\bexactly\b/.test(query.toLowerCase());
  return isExact ? match[1] : `${match[1]}+`;
}

function extractBathrooms(query: string): string | null {
  const match = query.toLowerCase().match(/(\d+)\s*\+?\s*(?:bathrooms?|baths?|ba)\b/);
  if (!match) return null;
  const isExact = /\bexactly\b/.test(query.toLowerCase());
  return isExact ? match[1] : `${match[1]}+`;
}

function extractParkingSpaces(query: string): string | null {
  const match = query.toLowerCase().match(/(\d+)\s*\+?\s*(?:parking\s+spaces?|parking\s+spots?|car\s+garages?)\b/);
  if (!match) return null;
  const isExact = /\bexactly\b/.test(query.toLowerCase());
  return isExact ? match[1] : `${match[1]}+`;
}

interface FeatureFlags {
  garage?: boolean;
  pool?: boolean;
  fireplace?: boolean;
  waterfront?: boolean;
  openHouse?: boolean;
}

function extractFeatures(query: string): FeatureFlags | null {
  const lower = query.toLowerCase();
  const features: FeatureFlags = {};
  if (/\bgarage\b/.test(lower)) features.garage = true;
  if (/\b(pool|swimming\s+pool)\b/.test(lower)) features.pool = true;
  if (/\bfireplaces?\b/.test(lower)) features.fireplace = true;
  if (/\b(waterfront|lakefront|oceanfront|beachfront)\b/.test(lower)) features.waterfront = true;
  if (/\bopen\s+house\b/.test(lower)) features.openHouse = true;
  return Object.keys(features).length > 0 ? features : null;
}

/**
 * Extract rental vs. purchase intent. Returns null when the query is silent
 * on this dimension (so we don't override an existing setting).
 */
function extractRental(query: string): boolean | null {
  const lower = query.toLowerCase();
  if (/\b(for\s+rent|rentals?|leases?|to\s+lease|to\s+rent)\b/.test(lower)) return true;
  if (/\b(for\s+sale|to\s+buy|to\s+purchase)\b/.test(lower)) return false;
  return null;
}

function extractStatus(query: string): string | null {
  const lower = query.toLowerCase();
  if (/\b(sold|recently\s+sold)\b/.test(lower)) return 'not-available-sold';
  if (/\bavailable\b/.test(lower)) return 'available';
  return null;
}

/**
 * Result returned by {@link parseNaturalLanguageQueryIntoFilters}. Pairs the
 * structured filter object with whatever portion of the query the parser
 * couldn't account for, so callers can decide whether to short-circuit (when
 * `unparsed` is `null` — the parser confidently understood the entire query)
 * or fall through to the AI (when `unparsed` is non-null — there's leftover
 * intent the deterministic patterns didn't capture).
 */
export interface ParseResult {
  filters: SearchParameters;
  unparsed: string | null;
}

const REGEX_SPECIAL_CHARS = /[.*+?^${}()|[\]\\]/g;
function escapeRegex(value: string): string {
  return value.replace(REGEX_SPECIAL_CHARS, '\\$&');
}

/**
 * Build the `unparsed` portion of a {@link ParseResult} by stripping every
 * token the extractors know how to recognize plus the location words we
 * actually resolved, then collapsing whitespace and dropping common filler
 * verbs/articles. Returns the leftover string when it's longer than 3 chars
 * (anything shorter is almost always residual punctuation or a stray
 * preposition we missed) or `null` when nothing meaningful remains.
 *
 * The pattern list mirrors the regexes used by each extractor — keep them in
 * sync. Anything an extractor might match needs a corresponding strip here,
 * otherwise we'd surface tokens as "unparsed" that we actually understood.
 */
function deriveUnparsed(trimmed: string, parsed: SearchParameters): string | null {
  let s = trimmed;

  s = s.replace(/\$\s*\d+(?:[.,]\d+)?\s*[km]?\b/gi, ' ');
  s = s.replace(/\b\d+(?:[.,]\d+)?\s*[km]\b/gi, ' ');

  s = s.replace(/\b\d+\s*\+?\s*(?:bedrooms?|beds?|br|bathrooms?|baths?|ba|parking\s+spaces?|parking\s+spots?|car\s+garages?)\b/gi, ' ');

  s = s.replace(/\b\d+(?:\s*(?:to|-)\s*\d+)?\s*(?:sq\s*ft|sqft|square\s+(?:feet|foot)|sf)\b/gi, ' ');

  s = s.replace(/\b(?:homes?|houses?|detached|single[\s-]family|bungalows?|condos?|apartments?|condominiums?|townhouses?|townhomes?|row\s+houses?|farms?|farmhouses?|acreages?|vacant\s+land|land|lots?|commercial|office|retail|industrial|real\s+estate|propert(?:y|ies)|listings?|places?)\b/gi, ' ');

  s = s.replace(/\b(?:garage|swimming\s+pool|pool|fireplaces?|waterfront|lakefront|oceanfront|beachfront|open\s+house)\b/gi, ' ');

  s = s.replace(/\b(?:for\s+rent|rentals?|leases?|to\s+lease|to\s+rent|for\s+sale|to\s+buy|to\s+purchase|recently\s+sold|sold|available)\b/gi, ' ');

  s = s.replace(/\b(?:under|over|below|above|less\s+than|more\s+than|between|budget|max(?:imum)?|min(?:imum)?|up\s+to|at\s+least|from|exactly)\b/gi, ' ');

  // Strip the location terms we actually resolved (city/neighbourhood/province
  // name + code + country word) so the user's geography input doesn't bleed
  // into `unparsed`.
  if (parsed.neighbourhood) {
    s = s.replace(new RegExp(`\\b${escapeRegex(parsed.neighbourhood)}\\b`, 'gi'), ' ');
  }
  if (parsed.city) {
    s = s.replace(new RegExp(`\\b${escapeRegex(parsed.city)}\\b`, 'gi'), ' ');
  }
  if (parsed.province) {
    s = s.replace(new RegExp(`\\b${escapeRegex(parsed.province)}\\b`, 'gi'), ' ');
    for (const [name, code] of Object.entries(ProvinceAndStateNameMappings)) {
      if (code.toLowerCase() === parsed.province.toLowerCase()) {
        s = s.replace(new RegExp(`\\b${name.replace(/\s+/g, '\\s+')}\\b`, 'gi'), ' ');
        break;
      }
    }
  }
  if (parsed.country) {
    for (const { pattern } of COUNTRY_NAME_PATTERNS) {
      const flags = pattern.flags.includes('g') ? pattern.flags : `${pattern.flags}g`;
      s = s.replace(new RegExp(pattern.source, flags), ' ');
    }
  }

  // Filler words: prepositions, intent verbs, articles, and common chat
  // pronouns that never carry filter intent on their own.
  s = s.replace(/\b(?:in|on|or|me|at|near|around|with|the|a|an|of|by|for|please|show|find|i|want|need|looking|some|any|and|to|just|maybe|like)\b/gi, ' ');

  s = s.replace(/[!?;,.]+/g, ' ');
  s = s.replace(/\s+/g, ' ').trim();

  return s.length > 3 ? s : null;
}

/**
 * Parse a free-text user query into a structured {@link ParseResult} using
 * deterministic keyword and pattern matching, optionally resolving locations
 * via the Go-Search predictions API.
 *
 * Returns `null` when the query is empty or contains nothing the parser can
 * recognize (no filters AND nothing meaningful left over). The caller should
 * fall back to the AI-powered conversational-search API in that case, or
 * whenever `unparsed` is non-null — leftover content signals there's intent
 * the deterministic parser couldn't account for.
 *
 * @param query Raw user input (unsanitized).
 * @returns A {@link ParseResult} with the extracted filters and any leftover
 *   query fragment, or `null` when nothing usable was inferred. Async because
 *   location extraction may invoke the predictions API.
 */
export async function parseNaturalLanguageQueryIntoFilters(query: string): Promise<ParseResult | null> {
  if (typeof query !== 'string') return null;
  const trimmed = normalizeNumberWords(query.trim());
  if (trimmed.length === 0) return null;

  const filters: SearchParameters = {};

  const location = await extractLocation(trimmed);
  if (location) {
    Object.assign(filters, location);
  }

  const homeType = extractHomeType(trimmed);
  if (homeType) {
    filters.homeType = homeType;
  }

  const bedrooms = extractBedrooms(trimmed);
  if (bedrooms) {
    filters.bedrooms = bedrooms;
  }

  const bathrooms = extractBathrooms(trimmed);
  if (bathrooms) {
    filters.bathrooms = bathrooms;
  }

  const parkingSpaces = extractParkingSpaces(trimmed);
  if (parkingSpaces) {
    filters.parkingSpaces = parkingSpaces;
  }

  const priceRange = extractPriceRange(trimmed);
  if (priceRange) {
    Object.assign(filters, priceRange);
  }

  const sqftRange = extractSqftRange(trimmed);
  if (sqftRange) {
    Object.assign(filters, sqftRange);
  }

  const features = extractFeatures(trimmed);
  if (features) {
    Object.assign(filters, features);
  }

  const rental = extractRental(trimmed);
  if (rental !== null) {
    filters.rental = rental;
  }

  const status = extractStatus(trimmed);
  if (status) {
    filters.status = status;
  }

  if (Object.keys(filters).length === 0) {
    // Without any extracted filters, there's nothing for the caller to act on
    // — the parser declines and the caller defers to the AI. We don't surface
    // an "empty filters with unparsed" result because no caller has a use for
    // it: the AI sees the original query regardless.
    return null;
  }

  const unparsed = deriveUnparsed(trimmed, filters);
  return { filters, unparsed };
}
